import React from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../types/auth';

interface RequirePermissionProps {
  /** Single permission or array of permissions to check */
  permission?: Permission | string;
  permissions?: (Permission | string)[];
  /** If true, requires ANY of the permissions (default: ALL) */
  any?: boolean;
  /** Content to show when access is denied (default: nothing) */
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Route/component guard based on RBAC permissions.
 *
 * Graceful fallback: if user has no roles (RBAC not configured),
 * children are always rendered.
 *
 * Usage:
 *   <RequirePermission permission={Permission.ADMIN_ES_MANAGEMENT}>
 *     <AdminDashboard />
 *   </RequirePermission>
 *
 *   <RequirePermission permissions={[Permission.CRM_VIEW, Permission.OFFERS_VIEW]} any>
 *     <CRMSection />
 *   </RequirePermission>
 */
export const RequirePermission: React.FC<RequirePermissionProps> = ({
  permission,
  permissions,
  any = false,
  fallback = null,
  children,
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, rbacActive } = usePermissions();

  // If RBAC not active, always show content
  if (!rbacActive) {
    return <>{children}</>;
  }

  // Single permission check
  if (permission) {
    if (hasPermission(permission)) {
      return <>{children}</>;
    }
    return <>{fallback}</>;
  }

  // Multiple permissions check
  if (permissions && permissions.length > 0) {
    const allowed = any
      ? hasAnyPermission(...permissions)
      : hasAllPermissions(...permissions);

    if (allowed) {
      return <>{children}</>;
    }
    return <>{fallback}</>;
  }

  // No permissions specified — always render
  return <>{children}</>;
};

export default RequirePermission;
